import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  Box,
  IconButton,
  Typography,
  Slide,
  styled,
  Stack,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
} from "@mui/material";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "white",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#00cc87",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#00cc87",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#666",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#00cc87",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  padding: "5px 30px",
  fontSize: "16px",
  marginTop: "20px",
  fontWeight: "bold",
  alignItems: { xs: "flex-end", md: "center" },
  textTransform: "none",
  background: "linear-gradient(45deg, #00cc87 30%, #f44336 90%)",
  boxShadow: "0 8px 20px rgba(211, 47, 47, 0.3)",
  "&:hover": {
    background: "linear-gradient(45deg, #b71c1c 30%, #00cc87 90%)",
    boxShadow: "0 12px 25px rgba(211, 47, 47, 0.4)",
    transform: "translateY(-2px)",
  },
  transition: "all 0.3s ease",
  "@media (max-width: 600px)": {
    width: "50%",
    fontSize: "12px",
    padding: "12px 30px",
  },
}));

const FileInputButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  padding: "8px 16px",
  fontSize: "14px",
  textTransform: "none",

  backgroundColor: "#f5f5f5",
  border: "1px solid #e0e0e0",
  "&:hover": {
    backgroundColor: "#eeeeee",
  },
}));

// Create a responsive stack component
const ResponsiveStack = styled(Stack)({
  "@media (max-width: 600px)": {
    flexDirection: "column !important",
    alignItems: "flex-end !important",
    "& > *": {
      width: "100% !important",
      marginBottom: "16px !important",
    },
  },
});

// Create a responsive container for checkboxes
const ResponsiveCheckboxContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "wrap",
  "@media (max-width: 600px)": {
    flexDirection: "column",
    alignItems: "flex-start",
    "& > *": {
      marginBottom: "8px !important",
    },
  },
});

export default function FormTwo() {
  const [formData, setFormData] = useState({
    email: "",
    referenceNumber: "",
    aadharNumber: "",
    aadharFile: null,
    panNumber: "",
    panFile: null,
    gstNumber: "",
    declaration: false,
    consent: false,
  });

  const [errors, setErrors] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Check if screen is mobile size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    // Initial check
    checkMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const validateField = (name, value) => {
    let errorMsg = "";

    switch (name) {
      case "email":
        if (!value) errorMsg = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          errorMsg = "Invalid email format";
        break;

      case "referenceNumber":
        if (!value.trim()) errorMsg = "Reference number is required";
        else if (!/^[A-Z0-9]{8,12}$/.test(value))
          errorMsg = "Invalid format (8-12 alphanumeric)";
        break;

      case "aadharNumber":
        if (!value) errorMsg = "Aadhar number is required";
        else if (!/^\d{4}\s?\d{4}\s?\d{4}$/.test(value))
          errorMsg = "Invalid Aadhar (12 digits)";
        break;

      case "aadharFile":
        if (!value) errorMsg = "Aadhar document is required";
        break;

      case "panNumber":
        if (!value) errorMsg = "PAN number is required";
        else if (!/^[A-Z]{5}\d{4}[A-Z]{1}$/.test(value))
          errorMsg = "Invalid PAN (e.g., ABCDE1234F)";
        break;

      case "panFile":
        if (!value) errorMsg = "PAN document is required";
        break;

      case "gstNumber":
        if (
          value &&
          !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
            value
          )
        )
          errorMsg = "Invalid GST format";
        break;

      case "declaration":
        if (!value) errorMsg = "You must confirm the declaration";
        break;

      case "consent":
        if (!value) errorMsg = "You must provide consent";
        break;

      default:
        break;
    }

    return errorMsg;
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));

    // Validate field on change
    if (errors[name]) {
      const errorMsg = validateField(name, fieldValue);
      setErrors((prev) => ({
        ...prev,
        [name]: errorMsg,
      }));
    }
  };

  const handleFileChange = (event, field) => {
    const file = event.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        [field]: file,
      }));

      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    const errorMsg = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: errorMsg,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((field) => {
      const errorMsg = validateField(field, formData[field]);
      if (errorMsg) {
        newErrors[field] = errorMsg;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      // Create form data for email API
      const emailFormData = new FormData();

      // Set email credentials and details
      emailFormData.append("smtpEmail", "psnfinanceblr@gmail.com");
      emailFormData.append("appPassword", "pnyl ysdr vfxk avmj");
      emailFormData.append("toEmail", "psnfinanceblr@gmail.com");
      emailFormData.append(
        "subject",
        `Document Submission - ${formData.referenceNumber}`
      );

      // Create message content using your form fields
      const message = `New document submission received.\n\nReference Number: ${
        formData.referenceNumber
      }\nEmail: ${formData.email}\nAadhar Number: ${
        formData.aadharNumber
      }\nPAN Number: ${formData.panNumber}\nGST Number: ${
        formData.gstNumber || "Not provided"
      }\nDeclaration Confirmed: ${
        formData.declaration ? "Yes" : "No"
      }\nConsent Given: ${formData.consent ? "Yes" : "No"}`;

      const html = `
        <h3>New Document Submission</h3>
        <p><strong>Reference Number:</strong> ${formData.referenceNumber}</p>
        <p><strong>Email:</strong> ${formData.email}</p>
        <p><strong>Aadhar Number:</strong> ${formData.aadharNumber}</p>
        <p><strong>PAN Number:</strong> ${formData.panNumber}</p>
        <p><strong>GST Number:</strong> ${
          formData.gstNumber || "Not provided"
        }</p>
        <p><strong>Declaration Confirmed:</strong> ${
          formData.declaration ? "Yes" : "No"
        }</p>
        <p><strong>Consent Given:</strong> ${
          formData.consent ? "Yes" : "No"
        }</p>
      `;

      emailFormData.append("message", message);
      emailFormData.append("html", html);

      // Add file attachments if they exist
      if (formData.aadharFile) {
        emailFormData.append("files", formData.aadharFile);
      }
      if (formData.panFile) {
        emailFormData.append("files", formData.panFile);
      }

      // Send email notification
      const emailResponse = await fetch(
        "https://email-sender.backendprod.com/api/send-email",
        {
          method: "POST",
          headers: {
            "X-API-Key": "Dinesh@@@@",
          },
          body: emailFormData,
        }
      );

      const emailResult = await emailResponse.json();

      if (emailResult.success) {
        setSnackbar({
          open: true,
          message: "Documents submitted successfully!",
          severity: "success",
        });

        // Reset form after successful submission
        setFormData({
          email: "",
          referenceNumber: "",
          aadharNumber: "",
          aadharFile: null,
          panNumber: "",
          panFile: null,
          gstNumber: "",
          declaration: false,
          consent: false,
        });
      } else {
        console.error("Email sending failed:", emailResult.error);
        setSnackbar({
          open: true,
          message: "Failed to submit documents. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      setSnackbar({
        open: true,
        message: "Failed to submit documents. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
      <Stack spacing={2} sx={{ mt: 2 }}>
        {/* User Identification (Email) */}

        <ResponsiveStack spacing={2} direction="row" alignItems="center">
          <StyledTextField
            required
            fullWidth
            id="email"
            label="User Identification (Email Address)"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.email}
            helperText={errors.email}
            variant="outlined"
          />

          {/* Application Reference Number */}
          <StyledTextField
            required
            fullWidth
            id="referenceNumber"
            label="Application Reference Number"
            name="referenceNumber"
            value={formData.referenceNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.referenceNumber}
            helperText={errors.referenceNumber || "e.g., ABC12345"}
            variant="outlined"
          />
        </ResponsiveStack>

        {/* Aadhar Number */}
        <ResponsiveStack spacing={2} direction="row" alignItems="center">
          <StyledTextField
            required
            fullWidth
            id="aadharNumber"
            label="Aadhar Number"
            name="aadharNumber"
            value={formData.aadharNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.aadharNumber}
            helperText={errors.aadharNumber || "12-digit number"}
            variant="outlined"
            inputProps={{
              maxLength: 14,
              pattern: "[0-9]{4} ?[0-9]{4} ?[0-9]{4}",
            }}
          />

          {/* Aadhar File Upload */}
          <Box sx={{ width: "100%" }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Upload Aadhar Document
            </Typography>
            <input
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
              id="aadharFile"
              type="file"
              onChange={(e) => handleFileChange(e, "aadharFile")}
            />
            <label htmlFor="aadharFile">
              <FileInputButton
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                fullWidth
              >
                {formData.aadharFile ? formData.aadharFile.name : "Choose File"}
              </FileInputButton>
            </label>
            {errors.aadharFile && (
              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 1 }}>
                {errors.aadharFile}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 1, display: "block" }}
            >
              (PDF, JPG, PNG up to 5MB)
            </Typography>
          </Box>
        </ResponsiveStack>

        {/* PAN Number */}
        <ResponsiveStack spacing={2} direction="row" alignItems="center">
          <StyledTextField
            required
            fullWidth
            id="panNumber"
            label="PAN No."
            name="panNumber"
            value={formData.panNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.panNumber}
            helperText={errors.panNumber || "e.g., ABCDE1234F"}
            variant="outlined"
            inputProps={{
              maxLength: 10,
              style: { textTransform: "uppercase" },
            }}
          />

          {/* PAN File Upload */}
          <Box sx={{ width: "100%" }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Upload PAN Document
            </Typography>
            <input
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
              id="panFile"
              type="file"
              onChange={(e) => handleFileChange(e, "panFile")}
            />
            <label htmlFor="panFile">
              <FileInputButton
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                fullWidth
              >
                {formData.panFile ? formData.panFile.name : "Choose File"}
              </FileInputButton>
            </label>
            {errors.panFile && (
              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 1 }}>
                {errors.panFile}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 1, display: "block" }}
            >
              (PDF, JPG, PNG up to 5MB)
            </Typography>
          </Box>
        </ResponsiveStack>

        {/* GST Number (Optional) */}
        <StyledTextField
          fullWidth
          id="gstNumber"
          label="GST Number (Optional)"
          name="gstNumber"
          value={formData.gstNumber}
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!errors.gstNumber}
          helperText={errors.gstNumber || "15-character alphanumeric"}
          variant="outlined"
        />

        {/* Declaration and Consent */}
        <ResponsiveCheckboxContainer>
          <FormControlLabel
            control={
              <Checkbox
                name="declaration"
                checked={formData.declaration}
                onChange={handleChange}
                color="primary"
              />
            }
            label="I declare that the documents provided are authentic and valid"
          />
          {errors.declaration && (
            <Typography variant="caption" color="error">
              {errors.declaration}
            </Typography>
          )}

          <FormControlLabel
            control={
              <Checkbox
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                color="primary"
              />
            }
            label="I consent to the processing of my documents for verification purposes"
          />
          {errors.consent && (
            <Typography variant="caption" color="error">
              {errors.consent}
            </Typography>
          )}
        </ResponsiveCheckboxContainer>
      </Stack>

      <Grid item xs={2}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <StyledButton
            type="submit"
            variant="contained"
            size="small"
            id="submit-documents"
            color="primary"
            fullWidth={isMobile}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit Documents"}
          </StyledButton>
        </Box>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
